/*
 * Quote request page (sections/quote-request.liquid).
 *
 * Every 3D file is checked in the browser as soon as it's added: we parse it, measure it, count holes in
 * the mesh, compare it to the build volume and draw a small preview. Bambu Studio / Orca 3MF projects are
 * split into their plates, drawn in the project's filament and paint colors, each with its own quantity.
 * The form then posts the details, the files and those results to the quote Worker (quote-worker/).
 */
(() => {
  if (customElements.get('quote-request-form')) return;

  // Keep in step with MAX_FILES / MAX_FILE_BYTES / MAX_TOTAL_BYTES / MAX_QUANTITY in quote-worker/src/index.ts.
  const MAX_FILES = 10;
  const MAX_FILE_MB = 50;
  const MAX_TOTAL_MB = 95;
  const MAX_QUANTITY = 100000;
  const MB = 1024 * 1024;
  const ACCEPTED = ['stl', '3mf', 'obj', 'step', 'stp'];
  const THUMB = 192;
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const READ_ERRORS = {
    'bad-stl': "We couldn't read this STL. It may be damaged or incomplete, so try exporting it again.",
    'bad-obj': "We couldn't read this OBJ. Try exporting it again.",
    'bad-zip': 'This 3MF looks damaged or incomplete. Try saving it again.',
    'bad-3mf': "We couldn't find a 3D model inside this 3MF.",
    'bad-step': "This doesn't look like a STEP file. Try exporting it again.",
    empty: 'This file has no 3D shapes in it.',
    default: "We couldn't read this file. Try exporting it again.",
  };

  /* ------------------------------------------------------------------------------------------
   * File analysis. Runs in a Web Worker so big files don't freeze the page. This function is
   * turned into a string to build the worker, so it must not use anything from outside itself.
   *
   * Every format ends up as "plates": lists of mesh instances (indexed mesh + transform + filament).
   * ---------------------------------------------------------------------------------------- */
  function analyzer() {
    const UNIT_MM = { micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4, foot: 304.8, meter: 1000 };
    const SIZE = 192;
    const MAX_PLATES = 60;
    const IDENTITY = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0];
    const FALLBACK = ['#8A96A3', '#D9534F', '#3B7DDD', '#F0AD4E', '#5CB85C', '#9B59B6', '#1ABC9C', '#E67E22'].map(hexColor);

    self.onmessage = async (event) => {
      const { id, ext, buffer } = event.data;
      try {
        const result = await analyze(ext, buffer);
        self.postMessage({ id, result }, result.plates ? result.plates.map((p) => p.thumb.buffer) : []);
      } catch (err) {
        self.postMessage({ id, error: (err && err.message) || 'default' });
      }
    };

    async function analyze(ext, buffer) {
      if (ext === 'step' || ext === 'stp') {
        const head = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 4096)));
        if (!head.includes('ISO-10303-21')) throw new Error('bad-step');
        return { kind: 'step' };
      }

      let groups;
      if (ext === 'stl') groups = [single(weld(parseSTL(buffer)))];
      else if (ext === 'obj') groups = [single(parseOBJ(new TextDecoder().decode(buffer)))];
      else groups = await parse3MF(buffer);

      const plates = groups.map(summarize).filter(Boolean);
      if (!plates.length) throw new Error('empty');
      return { kind: 'mesh', plates };
    }

    function single(mesh) {
      return { id: 1, name: '', hasNegative: false, filaments: null, embedded: null, instances: [{ mesh, m: IDENTITY, filament: 1, object: 0 }] };
    }

    // ---- STL and OBJ

    function parseSTL(buffer) {
      const view = new DataView(buffer);
      if (buffer.byteLength >= 84) {
        const count = view.getUint32(80, true);
        if (84 + count * 50 === buffer.byteLength) {
          const out = new Float32Array(count * 9);
          for (let t = 0, o = 96, k = 0; t < count; t++, o += 50) {
            for (let j = 0; j < 36; j += 4) out[k++] = view.getFloat32(o + j, true);
          }
          return out;
        }
      }
      const text = new TextDecoder().decode(buffer);
      if (!/^\s*solid/.test(text)) throw new Error('bad-stl');
      const values = [];
      const re = /vertex\s+(\S+)\s+(\S+)\s+(\S+)/g;
      let m;
      while ((m = re.exec(text))) values.push(+m[1], +m[2], +m[3]);
      if (!values.length) throw new Error('empty');
      if (values.some(Number.isNaN)) throw new Error('bad-stl');
      return new Float32Array(values.slice(0, values.length - (values.length % 9)));
    }

    /** STL has no shared vertices, so join corners that sit at the same spot (to 0.001 mm). */
    function weld(soup) {
      const vertexCount = soup.length / 3;
      let tableSize = 1;
      while (tableSize < vertexCount * 1.5) tableSize *= 2;
      const mask = tableSize - 1;
      const table = new Int32Array(tableSize).fill(-1);
      const first = new Uint32Array(vertexCount);
      const tris = new Uint32Array(vertexCount);
      let unique = 0;
      for (let v = 0; v < vertexCount; v++) {
        const qx = Math.round(soup[v * 3] * 1000);
        const qy = Math.round(soup[v * 3 + 1] * 1000);
        const qz = Math.round(soup[v * 3 + 2] * 1000);
        let h = (Math.imul(qx, 73856093) ^ Math.imul(qy, 19349663) ^ Math.imul(qz, 83492791)) & mask;
        for (;;) {
          const t = table[h];
          if (t === -1) {
            table[h] = unique;
            first[unique] = v;
            tris[v] = unique++;
            break;
          }
          const f = first[t] * 3;
          if (Math.round(soup[f] * 1000) === qx && Math.round(soup[f + 1] * 1000) === qy && Math.round(soup[f + 2] * 1000) === qz) {
            tris[v] = t;
            break;
          }
          h = (h + 1) & mask;
        }
      }
      const verts = new Float64Array(unique * 3);
      for (let u = 0; u < unique; u++) {
        verts[u * 3] = soup[first[u] * 3];
        verts[u * 3 + 1] = soup[first[u] * 3 + 1];
        verts[u * 3 + 2] = soup[first[u] * 3 + 2];
      }
      return { verts, tris, states: null, stats: null };
    }

    function parseOBJ(text) {
      const verts = [];
      const tris = [];
      for (const raw of text.split('\n')) {
        const line = raw.trim();
        if (line.startsWith('v ')) {
          const p = line.split(/\s+/);
          verts.push(+p[1], +p[2], +p[3]);
        } else if (line.startsWith('f ')) {
          const count = verts.length / 3;
          const idx = line
            .split(/\s+/)
            .slice(1)
            .map((s) => {
              const i = parseInt(s, 10);
              return i < 0 ? count + i : i - 1;
            });
          if (idx.some((i) => !(i >= 0 && i < count))) throw new Error('bad-obj');
          for (let k = 1; k + 1 < idx.length; k++) tris.push(idx[0], idx[k], idx[k + 1]);
        }
      }
      if (verts.some(Number.isNaN)) throw new Error('bad-obj');
      return { verts: new Float64Array(verts), tris: new Uint32Array(tris), states: null, stats: null };
    }

    // ---- 3MF

    async function parse3MF(buffer) {
      const files = await unzip(buffer, (name) => /\.model$/i.test(name) || /^\/?Metadata\/(model_settings|project_settings|Slic3r_PE)\.config$/i.test(name));
      const modelPaths = [...files.keys()].filter((p) => /\.model$/i.test(p));
      if (!modelPaths.length) throw new Error('bad-3mf');

      const objects = new Map();
      const paintCache = new Map();
      let buildPath = null;
      for (const path of modelPaths) {
        const xml = files.get(path);
        if (/<build[\s>]/.test(xml) && (!buildPath || /(^|\/)3dmodel\.model$/i.test(path))) buildPath = path;
        const objRe = /<object\b([^>]*)>([\s\S]*?)<\/object>/g;
        let m;
        while ((m = objRe.exec(xml))) {
          const type = attr(m[1], 'type');
          const entry = { mesh: null, components: [], skip: Boolean(type && type !== 'model') };
          if (!entry.skip) {
            if (m[2].includes('<mesh')) entry.mesh = parseMesh(m[2], paintCache);
            const compRe = /<component\b([^>]*?)\/?>/g;
            let c;
            while ((c = compRe.exec(m[2]))) {
              entry.components.push({ path: clean(attr(c[1], 'p:path') || path), id: attr(c[1], 'objectid'), matrix: matrix(attr(c[1], 'transform')) });
            }
          }
          objects.set(`${path}#${attr(m[1], 'id')}`, entry);
        }
      }
      if (!buildPath) throw new Error('bad-3mf');

      const root = files.get(buildPath);
      const unit = UNIT_MM[(attr(/<model\b([^>]*)>/.exec(root)?.[1] || '', 'unit') || 'millimeter').toLowerCase()] || 1;
      const units = [unit, 0, 0, 0, unit, 0, 0, 0, unit, 0, 0, 0];
      const build = /<build\b[^>]*>([\s\S]*?)<\/build>/.exec(root)?.[1] || '';
      const items = [];
      const itemRe = /<item\b([^>]*?)\/?>/g;
      let it;
      while ((it = itemRe.exec(build))) {
        items.push({ object: attr(it[1], 'objectid'), path: clean(attr(it[1], 'p:path') || buildPath), m: multiply(matrix(attr(it[1], 'transform')), units) });
      }

      // Bambu Studio / Orca: which parts are real (not modifiers, negative parts or support painters),
      // which filament each object and part uses, and which objects sit on which plate.
      const settings = files.get('Metadata/model_settings.config') || '';
      const objectMeta = new Map();
      for (const m of settings.matchAll(/<object id="(\d+)">([\s\S]*?)<\/object>/g)) {
        const parts = new Map();
        for (const p of m[2].matchAll(/<part id="(\d+)" subtype="([^"]+)">([\s\S]*?)<\/part>/g)) {
          parts.set(p[1], { subtype: p[2], filament: Number(meta(p[3], 'extruder')) || 0 });
        }
        const head = m[2].split('<part')[0];
        objectMeta.set(m[1], {
          filament: Number(meta(head, 'extruder')) || 1,
          parts,
          layerHeight: Number(meta(head, 'layer_height')) || 0,
          infill: percent(meta(head, 'sparse_infill_density')),
        });
      }
      const plates = [];
      for (const m of settings.matchAll(/<plate>([\s\S]*?)<\/plate>/g)) {
        plates.push({
          id: Number(meta(m[1], 'plater_id')) || plates.length + 1,
          name: meta(m[1], 'plater_name'),
          instances: [...m[1].matchAll(/<model_instance>([\s\S]*?)<\/model_instance>/g)].map((x) => ({
            object: meta(x[1], 'object_id'),
            instance: Number(meta(x[1], 'instance_id')) || 0,
          })),
        });
      }

      const itemsByObject = new Map();
      for (const item of items) {
        if (!itemsByObject.has(item.object)) itemsByObject.set(item.object, []);
        itemsByObject.get(item.object).push(item);
      }
      let specs = plates
        .map((plate) => {
          const picked = plate.instances.map((i) => {
            const list = itemsByObject.get(i.object) || [];
            return list[i.instance] || list[0];
          });
          return { id: plate.id, name: plate.name, items: [...new Set(picked.filter(Boolean))] };
        })
        .filter((spec) => spec.items.length);
      if (!specs.length) specs = [{ id: 1, name: '', items }];

      const filaments = parseFilaments(files);
      const print = parsePrintSettings(files);
      return specs.slice(0, MAX_PLATES).map((spec) => {
        const group = { id: spec.id, name: spec.name, hasNegative: false, filaments, instances: [], embedded: null };
        // Print settings saved in the project; the first object on the plate with its own override wins.
        let layerHeight = print ? print.layerHeight : null;
        let infill = print ? print.infill : null;
        let overridden = false;
        spec.items.forEach((item, index) => {
          const info = objectMeta.get(item.object);
          if (info && !overridden && (info.layerHeight || info.infill != null)) {
            layerHeight = info.layerHeight || layerHeight;
            infill = info.infill ?? infill;
            overridden = true;
          }
          collect(objects, item.path, item.object, item.m, { filament: info ? info.filament : 1, info, object: index }, group, 0);
        });
        if (layerHeight || infill != null) group.embedded = { layerHeight: layerHeight || null, infill };
        return group;
      });
    }

    function collect(objects, path, id, m, ctx, group, depth) {
      const entry = objects.get(`${path}#${id}`);
      if (!entry || entry.skip || depth > 16) return;
      if (entry.mesh) group.instances.push({ mesh: entry.mesh, m, filament: ctx.filament, object: ctx.object });
      for (const c of entry.components) {
        let filament = ctx.filament;
        const part = depth === 0 && ctx.info ? ctx.info.parts.get(c.id) : null;
        if (part && part.subtype !== 'normal_part') {
          if (part.subtype === 'negative_part') group.hasNegative = true;
          continue;
        }
        if (part && part.filament) filament = part.filament;
        collect(objects, c.path, c.id, multiply(c.matrix, m), { ...ctx, filament }, group, depth + 1);
      }
    }

    function parseMesh(body, paintCache) {
      const verts = [];
      let m;
      const vertexRe = /<vertex\s+x="([^"]*)"\s+y="([^"]*)"\s+z="([^"]*)"/g;
      while ((m = vertexRe.exec(body))) verts.push(+m[1], +m[2], +m[3]);
      if (!verts.length && body.includes('<vertex')) {
        const anyRe = /<vertex\b([^>]*)>/g;
        while ((m = anyRe.exec(body))) verts.push(+attr(m[1], 'x'), +attr(m[1], 'y'), +attr(m[1], 'z'));
      }
      if (verts.some(Number.isNaN)) throw new Error('bad-3mf');

      const count = verts.length / 3;
      const tris = [];
      const states = [];
      let painted = false;
      const triRe = /<triangle\s+v1="(\d+)"\s+v2="(\d+)"\s+v3="(\d+)"([^>]*)>/g;
      while ((m = triRe.exec(body))) {
        const a = +m[1];
        const b = +m[2];
        const c = +m[3];
        if (a >= count || b >= count || c >= count) continue;
        tris.push(a, b, c);
        let state = 0;
        if (m[4].length > 3) {
          const paint = /(?:paint_color|mmu_segmentation)="([0-9A-Fa-f]+)"/.exec(m[4]);
          if (paint) {
            state = paintCache.get(paint[1]);
            if (state === undefined) {
              state = paintState(paint[1]);
              paintCache.set(paint[1], state);
            }
            if (state) painted = true;
          }
        }
        states.push(state);
      }
      return { verts: new Float64Array(verts), tris: new Uint32Array(tris), states: painted ? Uint8Array.from(states) : null, stats: null };
    }

    /**
     * Color painting (Bambu "paint_color", Prusa "mmu_segmentation") stores each triangle as a subdivision
     * tree, as hex nibbles read from the end. We only need a preview, so we keep the filament that covers
     * most of the triangle. State 0 means "the part's own filament"; state n means filament n.
     */
    function paintState(code) {
      const nibbles = [];
      for (let i = code.length - 1; i >= 0; i--) nibbles.push(parseInt(code[i], 16));
      let pos = 0;
      const weights = new Map();
      const node = (weight) => {
        if (pos >= nibbles.length) throw new Error('paint');
        const n = nibbles[pos++];
        const sides = n & 3;
        if (sides) {
          for (let i = 0; i <= sides; i++) node(weight / (sides + 1));
          return;
        }
        const state = (n & 12) === 12 ? nibbles[pos++] + 3 : n >> 2;
        weights.set(state, (weights.get(state) || 0) + weight);
      };
      try {
        node(1);
      } catch {
        return 0;
      }
      let best = 0;
      let bestWeight = -1;
      for (const [state, weight] of weights) {
        if (weight > bestWeight) {
          best = state;
          bestWeight = weight;
        }
      }
      return Math.min(best, 255) || 0;
    }

    function parseFilaments(files) {
      const project = files.get('Metadata/project_settings.config');
      if (project) {
        try {
          const list = JSON.parse(project).filament_colour;
          if (Array.isArray(list) && list.length) return list.map(hexColor);
        } catch {
          // not JSON: fall through
        }
      }
      const prusa = files.get('Metadata/Slic3r_PE.config');
      if (prusa) {
        const read = (key) => (new RegExp(`;\\s*${key}\\s*=\\s*(.*)`).exec(prusa)?.[1] || '').split(';');
        const extruders = read('extruder_colour');
        const list = read('filament_colour').map((f, i) => hexColor(extruders[i]) || hexColor(f));
        if (list.some(Boolean)) return list;
      }
      return null;
    }

    /** Layer height (mm) and sparse infill (%) saved in a Bambu Studio / Orca / PrusaSlicer project. */
    function parsePrintSettings(files) {
      const project = files.get('Metadata/project_settings.config');
      if (project) {
        try {
          const settings = JSON.parse(project);
          return { layerHeight: Number(settings.layer_height) || null, infill: percent(settings.sparse_infill_density) };
        } catch {
          // not JSON: fall through
        }
      }
      const prusa = files.get('Metadata/Slic3r_PE.config');
      if (prusa) {
        const read = (key) => new RegExp(`;\\s*${key}\\s*=\\s*(.*)`).exec(prusa)?.[1];
        return { layerHeight: Number(read('layer_height')) || null, infill: percent(read('fill_density')) };
      }
      return null;
    }

    function percent(value) {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : null;
    }

    function hexColor(value) {
      const m = /^#?([0-9a-f]{6})/i.exec(String(value || '').trim());
      if (!m) return null;
      const n = parseInt(m[1], 16);
      return { rgb: [(n >> 16) & 255, (n >> 8) & 255, n & 255], hex: `#${m[1].toUpperCase()}` };
    }

    async function unzip(buffer, wanted) {
      const view = new DataView(buffer);
      const bytes = new Uint8Array(buffer);
      let end = -1;
      for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 65557); i--) {
        if (view.getUint32(i, true) === 0x06054b50) {
          end = i;
          break;
        }
      }
      if (end < 0) throw new Error('bad-zip');
      const count = view.getUint16(end + 10, true);
      let p = view.getUint32(end + 16, true);
      const files = new Map();
      for (let n = 0; n < count; n++) {
        if (p + 46 > buffer.byteLength || view.getUint32(p, true) !== 0x02014b50) throw new Error('bad-zip');
        const method = view.getUint16(p + 10, true);
        const compressed = view.getUint32(p + 20, true);
        const nameLength = view.getUint16(p + 28, true);
        const local = view.getUint32(p + 42, true);
        const name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLength));
        p += 46 + nameLength + view.getUint16(p + 30, true) + view.getUint16(p + 32, true);
        if (!wanted(name)) continue;
        const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
        if (start + compressed > buffer.byteLength) throw new Error('bad-zip');
        const data = bytes.subarray(start, start + compressed);
        let raw;
        if (method === 0) raw = data;
        else if (method === 8) {
          const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
          raw = new Uint8Array(await new Response(stream).arrayBuffer());
        } else throw new Error('bad-zip');
        files.set(clean(name), new TextDecoder().decode(raw));
      }
      return files;
    }

    function attr(attrs, name) {
      const m = new RegExp(`(?:^|\\s)${name.replace(':', '\\:')}="([^"]*)"`).exec(attrs);
      return m ? m[1] : '';
    }

    function meta(text, key) {
      const m = new RegExp(`key="${key}" value="([^"]*)"`).exec(text);
      return m ? m[1] : '';
    }

    function clean(path) {
      return path.replace(/^\/+/, '');
    }

    /** 3MF transforms are row-major 3x4: "m00 m01 m02 m10 m11 m12 m20 m21 m22 m30 m31 m32". */
    function matrix(text) {
      const v = text.trim().split(/\s+/).map(Number);
      return v.length === 12 && v.every(Number.isFinite) ? v : IDENTITY;
    }

    /** Apply a, then b. */
    function multiply(a, b) {
      const r = new Array(12);
      for (let i = 0; i < 4; i++) {
        const x = a[i * 3];
        const y = a[i * 3 + 1];
        const z = a[i * 3 + 2];
        const t = i === 3 ? 1 : 0;
        r[i * 3] = x * b[0] + y * b[3] + z * b[6] + t * b[9];
        r[i * 3 + 1] = x * b[1] + y * b[4] + z * b[7] + t * b[10];
        r[i * 3 + 2] = x * b[2] + y * b[5] + z * b[8] + t * b[11];
      }
      return r;
    }

    // ---- measurements

    /**
     * Holes: an edge used by exactly one triangle, counted per mesh using the file's own vertex indices.
     * Edges shared by 4+ triangles (touching shells, common in OpenSCAD and CAD exports) print fine,
     * so they are not reported.
     */
    function meshStats(mesh) {
      if (mesh.stats) return mesh.stats;
      const { verts, tris } = mesh;
      const vertexCount = verts.length / 3;
      const keys = new Float64Array(tris.length);
      let k = 0;
      let volume = 0;
      for (let t = 0; t < tris.length; t += 3) {
        const a = tris[t];
        const b = tris[t + 1];
        const c = tris[t + 2];
        if (a === b || b === c || a === c) continue;
        keys[k++] = a < b ? a * vertexCount + b : b * vertexCount + a;
        keys[k++] = b < c ? b * vertexCount + c : c * vertexCount + b;
        keys[k++] = c < a ? c * vertexCount + a : a * vertexCount + c;
        const ax = verts[a * 3];
        const ay = verts[a * 3 + 1];
        const az = verts[a * 3 + 2];
        const bx = verts[b * 3];
        const by = verts[b * 3 + 1];
        const bz = verts[b * 3 + 2];
        const cx = verts[c * 3];
        const cy = verts[c * 3 + 1];
        const cz = verts[c * 3 + 2];
        volume += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
      }
      const sorted = keys.subarray(0, k).sort();
      let openEdges = 0;
      for (let i = 0; i < k; ) {
        let j = i + 1;
        while (j < k && sorted[j] === sorted[i]) j++;
        if (j - i === 1) openEdges++;
        i = j;
      }
      mesh.stats = { openEdges, volume: volume / 6 };
      return mesh.stats;
    }

    function summarize(group) {
      let triangles = 0;
      for (const inst of group.instances) triangles += inst.mesh.tris.length / 3;
      if (!triangles) return null;

      const soup = new Float32Array(triangles * 9);
      const rgb = new Uint8Array(triangles * 3);
      const objectBounds = new Map();
      const used = new Map();
      let k = 0;
      let c = 0;
      let openEdges = 0;
      let volume = 0;
      let closed = !group.hasNegative;
      for (const { mesh, m, filament, object } of group.instances) {
        const stats = meshStats(mesh);
        openEdges += stats.openEdges;
        if (stats.openEdges) closed = false;
        const det = m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
        volume += Math.abs(stats.volume * det);

        const b = objectBounds.get(object) || [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
        const { verts, tris, states } = mesh;
        for (let t = 0; t < tris.length / 3; t++) {
          const color = colorFor((states && states[t]) || filament, group.filaments);
          for (let j = 0; j < 3; j++) {
            const v = tris[t * 3 + j] * 3;
            const x = verts[v];
            const y = verts[v + 1];
            const z = verts[v + 2];
            const wx = x * m[0] + y * m[3] + z * m[6] + m[9];
            const wy = x * m[1] + y * m[4] + z * m[7] + m[10];
            const wz = x * m[2] + y * m[5] + z * m[8] + m[11];
            soup[k++] = wx;
            soup[k++] = wy;
            soup[k++] = wz;
            if (wx < b[0]) b[0] = wx;
            if (wy < b[1]) b[1] = wy;
            if (wz < b[2]) b[2] = wz;
            if (wx > b[3]) b[3] = wx;
            if (wy > b[4]) b[4] = wy;
            if (wz > b[5]) b[5] = wz;
          }
          rgb[c++] = color.rgb[0];
          rgb[c++] = color.rgb[1];
          rgb[c++] = color.rgb[2];
          used.set(color.hex, (used.get(color.hex) || 0) + 1);
        }
        objectBounds.set(object, b);
      }

      const all = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
      for (const b of objectBounds.values()) {
        for (let a = 0; a < 3; a++) {
          all[a] = Math.min(all[a], b[a]);
          all[a + 3] = Math.max(all[a + 3], b[a + 3]);
        }
      }
      if (!all.every(Number.isFinite)) return null;
      const size = (b) => [b[3] - b[0], b[4] - b[1], b[5] - b[2]];
      return {
        id: group.id,
        name: group.name,
        objects: objectBounds.size,
        dims: size(all),
        objectDims: [...objectBounds.values()].map(size),
        triangles,
        openEdges,
        volumeCm3: closed ? volume / 1000 : null,
        colors: group.filaments ? [...used.entries()].sort((a, b) => b[1] - a[1]).map(([hex]) => hex) : [],
        embedded: group.embedded,
        thumb: render(soup, rgb, all),
      };
    }

    function colorFor(filament, filaments) {
      return (filaments && filaments[filament - 1]) || (filaments ? FALLBACK[0] : FALLBACK[(filament - 1) % FALLBACK.length] || FALLBACK[0]);
    }

    /** Small shaded isometric preview, drawn with a z-buffer. Returns RGBA pixels. */
    function render(soup, rgb, b) {
      const yaw = -Math.PI / 4;
      const pitch = Math.PI / 6;
      const cy = Math.cos(yaw);
      const sy = Math.sin(yaw);
      const cp = Math.cos(pitch);
      const sp = Math.sin(pitch);
      const mx = (b[0] + b[3]) / 2;
      const my = (b[1] + b[4]) / 2;
      const mz = (b[2] + b[5]) / 2;
      const project = (x, y, z, out, o) => {
        x -= mx;
        y -= my;
        z -= mz;
        const rx = cy * x - sy * y;
        const ry = sy * x + cy * y;
        out[o] = rx;
        out[o + 1] = z * cp + ry * sp;
        out[o + 2] = ry * cp - z * sp;
      };

      const corner = new Float64Array(3);
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < 8; i++) {
        project(i & 1 ? b[3] : b[0], i & 2 ? b[4] : b[1], i & 4 ? b[5] : b[2], corner, 0);
        minX = Math.min(minX, corner[0]);
        maxX = Math.max(maxX, corner[0]);
        minY = Math.min(minY, corner[1]);
        maxY = Math.max(maxY, corner[1]);
      }
      const scale = (SIZE * 0.86) / Math.max(maxX - minX, maxY - minY, 1e-6);
      const ox = SIZE / 2 - ((minX + maxX) / 2) * scale;
      const oy = SIZE / 2 + ((minY + maxY) / 2) * scale;

      const depth = new Float32Array(SIZE * SIZE).fill(Infinity);
      const shade = new Float32Array(SIZE * SIZE);
      const owner = new Uint32Array(SIZE * SIZE);
      const p = new Float64Array(9);
      const light = [-0.45, 0.75, -0.48];
      for (let i = 0, tri = 0; i < soup.length; i += 9, tri++) {
        for (let j = 0; j < 3; j++) project(soup[i + j * 3], soup[i + j * 3 + 1], soup[i + j * 3 + 2], p, j * 3);
        const ux = p[3] - p[0];
        const uy = p[4] - p[1];
        const uz = p[5] - p[2];
        const vx = p[6] - p[0];
        const vy = p[7] - p[1];
        const vz = p[8] - p[2];
        const nx = uy * vz - uz * vy;
        const ny = uz * vx - ux * vz;
        const nz = ux * vy - uy * vx;
        const len = Math.hypot(nx, ny, nz);
        if (!len) continue;
        const intensity = Math.abs((nx * light[0] + ny * light[1] + nz * light[2]) / len);

        const ax = ox + p[0] * scale;
        const ay = oy - p[1] * scale;
        const bx = ox + p[3] * scale;
        const by = oy - p[4] * scale;
        const cx = ox + p[6] * scale;
        const cyy = oy - p[7] * scale;
        const area = (bx - ax) * (cyy - ay) - (by - ay) * (cx - ax);
        if (!area) continue;
        const x0 = Math.max(0, Math.floor(Math.min(ax, bx, cx)));
        const x1 = Math.min(SIZE - 1, Math.ceil(Math.max(ax, bx, cx)));
        const y0 = Math.max(0, Math.floor(Math.min(ay, by, cyy)));
        const y1 = Math.min(SIZE - 1, Math.ceil(Math.max(ay, by, cyy)));
        for (let py = y0; py <= y1; py++) {
          const sy2 = py + 0.5;
          for (let px = x0; px <= x1; px++) {
            const sx = px + 0.5;
            const w0 = ((bx - sx) * (cyy - sy2) - (by - sy2) * (cx - sx)) / area;
            const w1 = ((cx - sx) * (ay - sy2) - (cyy - sy2) * (ax - sx)) / area;
            const w2 = 1 - w0 - w1;
            if (w0 < 0 || w1 < 0 || w2 < 0) continue;
            const z = w0 * p[2] + w1 * p[5] + w2 * p[8];
            const idx = py * SIZE + px;
            if (z < depth[idx]) {
              depth[idx] = z;
              shade[idx] = intensity;
              owner[idx] = tri;
            }
          }
        }
      }

      const pixels = new Uint8ClampedArray(SIZE * SIZE * 4);
      for (let i = 0; i < SIZE * SIZE; i++) {
        if (depth[i] === Infinity) continue;
        const s = shade[i];
        const t = owner[i] * 3;
        // Dark filaments get a grey sheen on lit faces, or black parts would render as a flat silhouette.
        const sheen = 90 * s * s * (1 - (0.3 * rgb[t] + 0.59 * rgb[t + 1] + 0.11 * rgb[t + 2]) / 255);
        const light = 0.42 + 0.58 * s;
        pixels[i * 4] = rgb[t] * light + sheen;
        pixels[i * 4 + 1] = rgb[t + 1] * light + sheen;
        pixels[i * 4 + 2] = rgb[t + 2] * light + sheen;
        pixels[i * 4 + 3] = 255;
      }
      return pixels;
    }
  }

  /* ---------------------------------------------------------------------------------------- */

  let worker = null;
  const pending = new Map();
  let nextJob = 0;

  function analyzeFile(file, ext) {
    return file.arrayBuffer().then(
      (buffer) =>
        new Promise((resolve) => {
          if (!worker) {
            try {
              const source = `(${analyzer.toString()})()`;
              worker = new Worker(URL.createObjectURL(new Blob([source], { type: 'text/javascript' })));
              worker.onmessage = (event) => {
                const done = pending.get(event.data.id);
                pending.delete(event.data.id);
                if (done) done(event.data);
              };
              worker.onerror = () => {
                for (const done of pending.values()) done({ error: 'unchecked' });
                pending.clear();
                worker = null;
              };
            } catch {
              resolve({ error: 'unchecked' });
              return;
            }
          }
          const id = ++nextJob;
          pending.set(id, resolve);
          worker.postMessage({ id, ext, buffer }, [buffer]);
        }),
      () => ({ error: 'default' }),
    );
  }

  const formatBytes = (bytes) => (bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
  const formatLength = (v) => (v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2));
  const formatDims = (d) => `${d.map(formatLength).join(' × ')} mm`;
  const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
  const fits = (dims, bed) => {
    const a = [...dims].sort((x, y) => y - x);
    const b = [...bed].sort((x, y) => y - x);
    return a.every((v, i) => v <= b[i] + 0.01);
  };
  const largestDims = (plate) => plate.objectDims.reduce((a, b) => (Math.max(...b) > Math.max(...a) ? b : a));
  const describePlate = (plate) => {
    const bits = [plate.objects > 1 ? `${plate.objects} parts, largest ${formatDims(largestDims(plate))}` : formatDims(plate.dims)];
    if (plate.volumeCm3 != null) bits.push(`${plate.volumeCm3 < 10 ? plate.volumeCm3.toFixed(1) : Math.round(plate.volumeCm3).toLocaleString()} cm³`);
    return bits.join(' · ');
  };

  const describeSettings = (e) =>
    [e.layerHeight ? `${+e.layerHeight.toFixed(3)} mm` : '', e.infill != null ? `${+e.infill.toFixed(1)}% infill` : ''].filter(Boolean).join(', ');

  class QuoteRequestForm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="file"]');
      this.drop = this.querySelector('[data-drop]');
      this.dropTitle = this.querySelector('[data-drop-title]');
      this.list = this.querySelector('[data-file-list]');
      this.summary = this.querySelector('[data-files-summary]');
      this.filesError = this.querySelector('[data-error="files"]');
      this.banner = this.querySelector('[data-banner]');
      this.submitButton = this.querySelector('[data-submit]');
      this.submitLabel = this.submitButton.textContent;
      this.progress = this.querySelector('[data-progress]');
      this.success = this.querySelector('[data-success]');
      this.items = [];
      this.qualities = [
        ['high', 'High', this.dataset.qualityHigh],
        ['standard', 'Standard', this.dataset.qualityStandard],
        ['draft', 'Draft', this.dataset.qualityDraft],
      ];

      const bed = (this.dataset.bed || '').split(/[^\d.]+/).map(Number).filter((n) => n > 0);
      this.bed = bed.length === 3 ? bed : [256, 256, 256];

      this.input.addEventListener('change', () => {
        this.addFiles(this.input.files);
        this.input.value = '';
      });
      for (const type of ['dragenter', 'dragover']) {
        this.drop.addEventListener(type, (e) => {
          e.preventDefault();
          this.drop.classList.add('is-dragover');
        });
      }
      for (const type of ['dragleave', 'drop']) {
        this.drop.addEventListener(type, (e) => {
          e.preventDefault();
          this.drop.classList.remove('is-dragover');
        });
      }
      this.drop.addEventListener('drop', (e) => this.addFiles(e.dataTransfer.files));
      // A file dropped just outside the box shouldn't make the browser navigate away from the form.
      for (const type of ['dragover', 'drop']) {
        window.addEventListener(type, (e) => {
          if (e.dataTransfer && [...e.dataTransfer.types].includes('Files')) e.preventDefault();
        });
      }

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });
      this.form.addEventListener('input', (e) => {
        if (e.target.getAttribute('aria-invalid') === 'true') this.validateField(e.target);
      });
      this.form.addEventListener('focusout', (e) => {
        if (e.target.matches('[data-validate]') && e.target.value) this.validateField(e.target);
      });
    }

    // ---- files

    addFiles(fileList) {
      this.setError(this.filesError, '');
      for (const file of Array.from(fileList)) {
        if (this.items.some((i) => i.file.name === file.name && i.file.size === file.size)) continue;
        if (this.items.length >= MAX_FILES) {
          this.setError(this.filesError, `You can send up to ${MAX_FILES} files per request. For more, send a second request or email ${this.dataset.contactEmail}.`);
          break;
        }
        const ext = (file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase();
        const item = { file, ext, state: 'checking', kind: 'file', meta: '', notes: [], plates: null, quantity: 1, quality: 'standard' };
        this.items.push(item);
        this.renderItem(item);

        let job;
        if (!ACCEPTED.includes(ext)) job = Promise.resolve({ error: 'type' });
        else if (file.size === 0) job = Promise.resolve({ error: 'empty' });
        else if (file.size > MAX_FILE_MB * MB) job = Promise.resolve({ error: 'too-big' });
        else job = analyzeFile(file, ext);
        item.done = job.then((message) => this.finish(item, message));
      }
      this.updateSummary();
    }

    finish(item, message) {
      if (!this.items.includes(item)) return;
      const notes = [];
      item.plates = null;
      item.meta = '';

      if (message.error === 'type') notes.push(['error', 'We accept STL, 3MF, OBJ and STEP files.']);
      else if (message.error === 'too-big') notes.push(['error', `This file is over the ${MAX_FILE_MB} MB limit. Try a 3MF, which is compressed, or email it to ${this.dataset.contactEmail}.`]);
      else if (message.error === 'unchecked') item.meta = "We'll check this file when we quote.";
      else if (message.error) notes.push(['error', READ_ERRORS[message.error] || READ_ERRORS.default]);
      else if (message.result.kind === 'step') {
        item.kind = 'step';
        item.meta = 'STEP file';
        notes.push(['ok', "Ready to send. We'll review the geometry when we quote."]);
      } else {
        item.kind = 'mesh';
        item.plates = message.result.plates.map((plate) => ({ ...plate, quantity: 1, quality: plate.embedded ? '3mf' : 'standard', notes: this.plateNotes(plate) }));
        if (item.plates.length === 1) {
          item.meta = describePlate(item.plates[0]);
          notes.push(...item.plates[0].notes);
          if (!notes.length) notes.push(['ok', 'Looks good']);
        } else {
          item.meta = `${item.plates.length} plates`;
          const flagged = item.plates.filter((p) => p.notes.length).length;
          notes.push(flagged ? ['warn', `${flagged} of ${item.plates.length} plates have a note below.`] : ['ok', 'Looks good']);
        }
      }

      item.notes = notes;
      item.state = notes.some(([l]) => l === 'error') ? 'error' : notes.some(([l]) => l === 'warn') ? 'warn' : 'ok';
      this.renderItem(item);
      this.updateSummary();
    }

    plateNotes(plate) {
      const notes = [];
      if (plate.objectDims.some((d) => !fits(d, this.bed))) {
        notes.push(['warn', `${plate.objects > 1 ? 'Some parts are' : 'This part is'} bigger than our ${this.bed.join(' × ')} mm build area. We can often split large parts, so mention it in your notes.`]);
      }
      const longest = Math.max(...largestDims(plate));
      if (longest < 2) {
        notes.push(['warn', `This is only ${formatLength(longest)} mm across. If it was exported in inches or meters, tell us in your notes.`]);
      }
      if (plate.openEdges) {
        notes.push(['warn', `The mesh has holes (${plural(plate.openEdges, 'open edge')}). We can usually repair that, but check your export if you can.`]);
      }
      return notes;
    }

    renderItem(item) {
      if (!item.el) {
        item.el = document.createElement('li');
        this.list.appendChild(item.el);
      }
      const li = item.el;
      li.className = `qr-file qr-file--${item.state}`;
      li.replaceChildren();

      const row = document.createElement('div');
      row.className = 'qr-file__row';
      const thumb = document.createElement('div');
      thumb.className = 'qr-file__thumb';
      if (item.plates) thumb.appendChild(this.canvas(item.plates[0].thumb));
      else if (item.state === 'checking') thumb.innerHTML = '<span class="qr-spinner" aria-hidden="true"></span>';
      else thumb.textContent = (item.ext || '?').toUpperCase().slice(0, 4);

      const body = document.createElement('div');
      body.className = 'qr-file__body';
      const name = this.text('p', 'qr-file__name', item.file.name);
      name.appendChild(this.text('span', 'qr-file__size', formatBytes(item.file.size)));
      body.appendChild(name);
      const meta = item.state === 'checking' ? 'Checking file…' : item.meta;
      if (meta) {
        const p = this.text('p', 'qr-file__meta', meta);
        if (item.plates && item.plates.length === 1) this.appendSwatches(p, item.plates[0].colors);
        body.appendChild(p);
      }
      for (const [level, text] of item.notes) body.appendChild(this.text('p', `qr-note qr-note--${level}`, text));

      const multi = item.plates && item.plates.length > 1;
      if (!multi && item.state !== 'checking' && item.state !== 'error') {
        const target = item.plates ? item.plates[0] : item;
        body.appendChild(this.controls(target, item.file.name, 1, target.embedded));
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'qr-file__remove';
      remove.setAttribute('aria-label', `Remove ${item.file.name}`);
      remove.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      remove.addEventListener('click', () => this.removeItem(item));

      row.append(thumb, body, remove);
      li.appendChild(row);

      if (multi) {
        const plates = document.createElement('ul');
        plates.className = 'qr-plates';
        plates.setAttribute('role', 'list');
        for (const plate of item.plates) {
          const label = plate.name ? `Plate ${plate.id}: ${plate.name}` : `Plate ${plate.id}`;
          const pli = document.createElement('li');
          pli.className = 'qr-plate';
          const pthumb = document.createElement('div');
          pthumb.className = 'qr-file__thumb';
          pthumb.appendChild(this.canvas(plate.thumb));
          const pbody = document.createElement('div');
          pbody.className = 'qr-file__body';
          const title = this.text('p', 'qr-file__name', plate.name || `Plate ${plate.id}`);
          if (plate.name) title.appendChild(this.text('span', 'qr-file__size', `Plate ${plate.id}`));
          pbody.appendChild(title);
          const pmeta = this.text('p', 'qr-file__meta', describePlate(plate));
          this.appendSwatches(pmeta, plate.colors);
          pbody.appendChild(pmeta);
          for (const [level, text] of plate.notes) pbody.appendChild(this.text('p', `qr-note qr-note--${level}`, text));
          pbody.appendChild(this.controls(plate, label, 0, plate.embedded));
          pli.append(pthumb, pbody);
          plates.appendChild(pli);
        }
        li.appendChild(plates);
      }
    }

    text(tag, className, value) {
      const el = document.createElement(tag);
      el.className = className;
      el.textContent = value;
      return el;
    }

    canvas(pixels) {
      const canvas = document.createElement('canvas');
      canvas.width = THUMB;
      canvas.height = THUMB;
      canvas.getContext('2d').putImageData(new ImageData(new Uint8ClampedArray(pixels), THUMB, THUMB), 0, 0);
      return canvas;
    }

    appendSwatches(el, colors) {
      if (!colors || !colors.length) return;
      const wrap = document.createElement('span');
      wrap.className = 'qr-swatches';
      for (const hex of colors.slice(0, 8)) {
        const swatch = document.createElement('span');
        swatch.className = 'qr-swatch';
        swatch.style.background = hex;
        swatch.title = hex;
        wrap.appendChild(swatch);
      }
      el.appendChild(wrap);
    }

    controls(target, label, min, embedded) {
      const row = document.createElement('div');
      row.className = 'qr-controls';
      row.append(this.quantity(target, label, min), this.qualityPicker(target, label, embedded));
      return row;
    }

    /** Quality bound to target.quality. "3MF settings" is only offered when the project saved some. */
    qualityPicker(target, label, embedded) {
      const wrap = document.createElement('label');
      wrap.className = 'qr-quality';
      wrap.appendChild(this.text('span', 'qr-qty-label', 'Quality'));
      const select = document.createElement('select');
      select.className = 'qr-quality__select';
      select.setAttribute('aria-label', `Quality for ${label}`);
      const options = embedded ? [['3mf', '3MF settings', describeSettings(embedded)], ...this.qualities] : this.qualities;
      for (const [value, name, detail] of options) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = detail ? `${name} (${detail})` : name;
        select.appendChild(option);
      }
      select.value = target.quality;
      select.addEventListener('change', () => {
        target.quality = select.value;
      });
      wrap.appendChild(select);
      return wrap;
    }

    /** Quantity stepper bound to target.quantity. Plates can go to 0 (not needed); whole files can't. */
    quantity(target, label, min) {
      const row = document.createElement('div');
      row.className = 'qr-qty-row';
      row.appendChild(this.text('span', 'qr-qty-label', 'Quantity'));
      const box = document.createElement('div');
      box.className = 'qr-qty';
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.min = String(min);
      input.max = String(MAX_QUANTITY);
      input.step = '1';
      input.value = String(target.quantity);
      input.setAttribute('aria-label', `Quantity for ${label}`);
      const set = (value) => {
        const n = Math.round(Number(value));
        target.quantity = Number.isFinite(n) ? Math.min(MAX_QUANTITY, Math.max(min, n)) : min;
        input.value = String(target.quantity);
        row.classList.toggle('is-zero', target.quantity === 0);
        this.updateSummary();
      };
      const button = (text, delta, name) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = text;
        b.setAttribute('aria-label', `${name} for ${label}`);
        b.addEventListener('click', () => set(target.quantity + delta));
        return b;
      };
      input.addEventListener('input', () => {
        const n = Number(input.value);
        if (input.value !== '' && Number.isInteger(n) && n >= min && n <= MAX_QUANTITY) {
          target.quantity = n;
          row.classList.toggle('is-zero', n === 0);
          this.updateSummary();
        }
      });
      input.addEventListener('change', () => set(input.value));
      box.append(button('−', -1, 'Decrease quantity'), input, button('+', 1, 'Increase quantity'));
      row.appendChild(box);
      row.classList.toggle('is-zero', target.quantity === 0);
      return row;
    }

    removeItem(item) {
      this.items = this.items.filter((i) => i !== item);
      item.el.remove();
      this.setError(this.filesError, '');
      this.updateSummary();
      this.input.focus();
    }

    totalBytes() {
      return this.items.reduce((sum, i) => sum + i.file.size, 0);
    }

    partsFor(item) {
      return item.plates ? item.plates.reduce((sum, p) => sum + p.quantity, 0) : item.quantity;
    }

    updateSummary() {
      const count = this.items.length;
      const total = this.totalBytes();
      const parts = this.items.filter((i) => i.state !== 'error' && i.state !== 'checking').reduce((sum, i) => sum + this.partsFor(i), 0);
      const over = total > MAX_TOTAL_MB * MB;
      this.summary.textContent = count
        ? `${plural(count, 'file')}${parts ? ` · ${plural(parts, 'part')}` : ''} · ${formatBytes(total)}${over ? ` (the limit is ${MAX_TOTAL_MB} MB in total)` : ''}`
        : '';
      this.summary.classList.toggle('is-over', over);
      this.drop.classList.toggle('has-files', count > 0);
      this.dropTitle.innerHTML = count ? 'Add more files' : 'Drop files here or <u>browse</u>';
    }

    // ---- validation

    validateField(input) {
      const value = input.value.trim();
      let message = '';
      if (input.name === 'name' && !value) message = 'Please enter your name.';
      if (input.name === 'email') message = !value ? 'Please enter your email.' : EMAIL.test(value) ? '' : "That email address doesn't look right.";
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      this.setError(this.querySelector(`[data-error="${input.name}"]`), message);
      return !message;
    }

    setError(el, message) {
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    filesProblem() {
      if (!this.items.length) return 'Add at least one 3D file.';
      if (this.items.some((i) => i.state === 'error')) return 'Remove or replace the files marked in red.';
      const empty = this.items.find((i) => i.state !== 'checking' && this.partsFor(i) < 1);
      if (empty) return `Set a quantity for at least one plate in ${empty.file.name}.`;
      if (this.totalBytes() > MAX_TOTAL_MB * MB) return `Files can be up to ${MAX_TOTAL_MB} MB in total. Send the rest in a second request.`;
      return '';
    }

    payload(item) {
      if (!item.plates) return { kind: item.kind, quantity: item.quantity, quality: item.quality };
      return {
        kind: 'mesh',
        plates: item.plates.map((p) => ({
          id: p.id,
          name: p.name,
          quantity: p.quantity,
          quality: p.quality,
          layerHeight: p.embedded ? p.embedded.layerHeight : null,
          infill: p.embedded ? p.embedded.infill : null,
          dims: p.dims,
          objects: p.objects,
          triangles: p.triangles,
          volumeCm3: p.volumeCm3,
          openEdges: p.openEdges,
          colors: p.colors,
          notes: p.notes.filter(([level]) => level === 'warn').map(([, text]) => text),
        })),
      };
    }

    // ---- sending

    async submit() {
      if (this.sending) return;
      this.setError(this.banner, '');

      const invalid = [...this.form.querySelectorAll('[data-validate]')].filter((f) => !this.validateField(f));
      const filesMessage = this.filesProblem();
      this.setError(this.filesError, filesMessage);
      if (invalid.length || filesMessage) {
        (invalid[0] || this.input).focus();
        if (!invalid.length) this.drop.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }

      if (this.items.some((i) => i.state === 'checking')) {
        this.setSending(true, 'Checking files…');
        await Promise.all(this.items.map((i) => i.done));
        this.setSending(false);
        this.submit();
        return;
      }

      if (!this.dataset.endpoint) {
        this.setError(this.banner, `Quote requests can't be sent right now. Please email your files to ${this.dataset.contactEmail}.`);
        return;
      }

      const data = new FormData(this.form);
      for (const item of this.items) data.append('files', item.file, item.file.name);
      data.append('analysis', JSON.stringify(this.items.map((i) => this.payload(i))));
      data.append('page', location.href);

      this.setSending(true, 'Sending…');
      const response = await this.upload(`${this.dataset.endpoint.replace(/\/+$/, '')}/quotes`, data);
      this.setSending(false);

      if (response.ok) {
        this.showSuccess(response.body.id, String(data.get('email')));
      } else {
        this.setError(
          this.banner,
          (response.body && response.body.error) || `We couldn't send your request. Check your connection and try again, or email ${this.dataset.contactEmail}.`,
        );
        if (window.turnstile) window.turnstile.reset();
      }
    }

    upload(url, data) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.responseType = 'json';
        xhr.upload.addEventListener('progress', (e) => {
          if (!e.lengthComputable) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          this.progress.firstElementChild.style.width = `${pct}%`;
          this.submitButton.textContent = pct < 100 ? `Uploading… ${pct}%` : 'Sending…';
        });
        xhr.addEventListener('load', () => {
          const body = xhr.response;
          resolve({ ok: xhr.status >= 200 && xhr.status < 300 && body && body.ok, body });
        });
        xhr.addEventListener('error', () => resolve({ ok: false, body: null }));
        xhr.send(data);
      });
    }

    setSending(sending, label) {
      this.sending = sending;
      this.submitButton.disabled = sending;
      this.submitButton.textContent = sending ? label : this.submitLabel;
      this.progress.hidden = !sending || label !== 'Sending…';
      this.progress.firstElementChild.style.width = '0%';
      this.form.setAttribute('aria-busy', String(sending));
    }

    showSuccess(reference, email) {
      const text = this.success.querySelector('[data-success-text]');
      text.replaceChildren();
      for (const part of text.dataset.template.split(/(\[email\]|\[reference\])/)) {
        if (part === '[email]' || part === '[reference]') {
          const strong = document.createElement('strong');
          strong.textContent = part === '[email]' ? email : reference;
          text.appendChild(strong);
        } else {
          text.appendChild(document.createTextNode(part));
        }
      }
      this.form.hidden = true;
      this.success.hidden = false;
      this.success.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.success.focus({ preventScroll: true });
    }
  }

  customElements.define('quote-request-form', QuoteRequestForm);
})();
